import React, { useEffect, useState } from "react";
import TrashIcon from "assets/icons/HeroIcons/TrashIcon";
import apiClient from "services/apiClient";
import { MaterialUiSelect } from "../Select";
import { ChoicesByUrl, MetaInfo } from "./types";

interface MatrixChoice {
  text: string;
  value: any;
}

interface MatrixColumn {
  name: string;
  title?: string;
  cellType?: string;
  inputType?: string;
  isRequired?: boolean;
  min?: number;
  max?: number;
  choices?: MatrixChoice[];
  choicesByUrl?: ChoicesByUrl | null;
}

interface DynamicMatrixFilterInputsProps {
  rowIndex: number;
  values: any;
  handleBlur: (event: any) => void;
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean,
  ) => void;
  metaInfo?: MetaInfo;
  otherQueryFilterErrors: any;
  otherQueryFilterTouched: any;
}

const normalizeChoice = (
  choice: any,
  titleName = "text",
  valueName = "value",
): MatrixChoice => {
  if (choice !== null && typeof choice === "object") {
    const value = choice[valueName] ?? choice.value ?? choice.text;
    return {
      text: String(choice[titleName] ?? choice.text ?? value ?? ""),
      value,
    };
  }

  return { text: String(choice ?? ""), value: choice };
};

const getNonNegativeInteger = (value: any, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : fallback;
};

const getChoiceValue = (choice: any) =>
  choice && typeof choice === "object" ? choice.value : choice;

const DynamicMatrixDropdownCell: React.FC<{
  column: MatrixColumn;
  currentValue: any;
  error?: string;
  showLabel: boolean;
  touched?: boolean;
  updateCell: (columnName: string, value: any) => void;
}> = ({ column, currentValue, error, showLabel, touched, updateCell }) => {
  const [options, setOptions] = useState<MatrixChoice[]>(() =>
    (column.choices ?? []).map((choice) => normalizeChoice(choice)),
  );

  useEffect(() => {
    let isActive = true;
    const source = column.choicesByUrl;

    if (!source?.url) {
      setOptions(
        (column.choices ?? []).map((choice) => normalizeChoice(choice)),
      );
      return () => {
        isActive = false;
      };
    }

    apiClient
      .get(source.url)
      .then(({ data }) => {
        if (!isActive) return;
        const choices = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.items)
              ? data.items
              : [];
        setOptions(
          (choices.length > 0 ? choices : column.choices ?? []).map(
            (choice: any) =>
              normalizeChoice(
                choice,
                source.titleName || "text",
                source.valueName || "value",
              ),
          ),
        );
      })
      .catch(() => {
        if (isActive) {
          setOptions(
            (column.choices ?? []).map((choice) => normalizeChoice(choice)),
          );
        }
      });

    return () => {
      isActive = false;
    };
  }, [
    column.choices,
    column.choicesByUrl?.titleName,
    column.choicesByUrl?.url,
    column.choicesByUrl?.valueName,
  ]);

  return (
    <div className="flex min-w-0 flex-col justify-center">
      {showLabel && (
        <p className="mb-1 text-xs font-semibold text-textNormal">
          {column.title || column.name}
        </p>
      )}
      <MaterialUiSelect
        options={options}
        labelKey="text"
        valueKey="value"
        initialValue={
          currentValue && typeof currentValue === "object"
            ? currentValue.value
            : currentValue
        }
        error={touched ? error : undefined}
        handleChange={(choice: MatrixChoice) => updateCell(column.name, choice)}
        height={42}
        width="100%"
        className="border-none capitalize"
      />
    </div>
  );
};

const DynamicMatrixFilterInputs: React.FC<DynamicMatrixFilterInputsProps> = ({
  rowIndex,
  values,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  metaInfo: availableMetaInfo,
  otherQueryFilterErrors,
  otherQueryFilterTouched,
}) => {
  const filter = values?.otherQueryFilter?.[rowIndex];
  const metaInfo = filter?.metaInfo ?? availableMetaInfo ?? {};
  const configuredMin = getNonNegativeInteger(metaInfo.minRowCount, 0);
  const configuredMax = getNonNegativeInteger(
    metaInfo.maxRowCount,
    Number.MAX_SAFE_INTEGER,
  );
  const minRowCount = configuredMin;
  const maxRowCount = Math.max(configuredMin, configuredMax);
  const initialRowCount = Math.min(
    maxRowCount,
    Math.max(
      minRowCount,
      getNonNegativeInteger(metaInfo.rowCount, minRowCount),
    ),
  );
  const storedValue = filter?.value;
  const storedRows = Array.isArray(storedValue)
    ? storedValue
    : storedValue &&
        typeof storedValue === "object" &&
        Object.keys(storedValue).length > 0
      ? [storedValue]
      : [];
  const rows = storedRows.slice(0, maxRowCount);
  const columns: MatrixColumn[] = Array.isArray(filter?.metaInfo?.columns)
    ? filter.metaInfo.columns
    : [];

  useEffect(() => {
    const hasInitializedRows = Array.isArray(storedValue);
    const exceedsMaximum = storedRows.length > maxRowCount;
    const targetRowCount = hasInitializedRows
      ? Math.min(maxRowCount, Math.max(minRowCount, rows.length))
      : initialRowCount;

    if (
      hasInitializedRows &&
      !exceedsMaximum &&
      rows.length === targetRowCount
    ) {
      return;
    }

    const nextRows = rows.slice(0, targetRowCount);
    while (nextRows.length < targetRowCount) nextRows.push({});
    setFieldValue(`otherQueryFilter[${rowIndex}].value`, nextRows);
  }, [
    initialRowCount,
    maxRowCount,
    minRowCount,
    rowIndex,
    rows.length,
    setFieldValue,
    storedRows.length,
    storedValue,
  ]);

  const updateCell = (
    matrixRowIndex: number,
    columnName: string,
    value: any,
  ) => {
    setFieldValue(
      `otherQueryFilter[${rowIndex}].value`,
      rows.map((row: any, index: number) =>
        index === matrixRowIndex ? { ...row, [columnName]: value } : row,
      ),
    );
  };

  const addRow = () => {
    if (rows.length >= maxRowCount || metaInfo.allowAddRows === false) return;
    setFieldValue(`otherQueryFilter[${rowIndex}].value`, [...rows, {}]);
  };

  const removeRow = (matrixRowIndex: number) => {
    if (rows.length <= minRowCount || metaInfo.allowRemoveRows === false) {
      return;
    }
    if (
      metaInfo.confirmDelete &&
      !window.confirm(
        metaInfo.confirmDeleteText || "Do you want to remove this row?",
      )
    ) {
      return;
    }
    setFieldValue(
      `otherQueryFilter[${rowIndex}].value`,
      rows.filter((_: any, index: number) => index !== matrixRowIndex),
    );
    setFieldTouched(`otherQueryFilter[${rowIndex}].value`, false, false);
  };

  if (columns.length === 0) {
    return (
      <p className="py-3 text-sm italic text-textNoSelection">
        No filterable matrix columns are configured.
      </p>
    );
  }

  const canAddRows =
    metaInfo.allowAddRows !== false && rows.length < maxRowCount;
  const canRemoveRows =
    metaInfo.allowRemoveRows !== false && rows.length > minRowCount;
  const showHeader = metaInfo.showHeader !== false;
  const matrixGridColumns = `${columns
    .map(() => "minmax(240px, 1fr)")
    .join(" ")}${canRemoveRows ? " 56px" : ""}`;

  return (
    <div className="flex w-full flex-col gap-2 py-2">
      <div className="w-full overflow-x-auto">
        <div className="w-full min-w-max">
          {showHeader && (
            <div
              className="grid items-stretch"
              style={{ gridTemplateColumns: matrixGridColumns }}
            >
              {columns.map((column, columnIndex) => (
                <div
                  className={`flex min-h-10 items-center justify-center bg-bgBluish_2 px-4 py-1.5 text-center text-sm font-semibold text-textNormal dark:bg-secondaryMid dark:text-textMain ${columnIndex === 0 ? "rounded-l-md" : ""} ${columnIndex === columns.length - 1 ? "rounded-r-md" : ""}`}
                  key={column.name}
                >
                  {column.title || column.name}
                  {column.isRequired ? " *" : ""}
                </div>
              ))}
              {canRemoveRows && <div aria-hidden="true" />}
            </div>
          )}

          <div className="flex flex-col gap-1 pt-2">
            {rows.map((matrixRow: any, matrixRowIndex: number) => (
              <div
                className="grid items-center gap-x-4"
                key={matrixRowIndex}
                style={{ gridTemplateColumns: matrixGridColumns }}
              >
                {columns.map((column) => {
                  const cellType =
                    !column.cellType || column.cellType === "default"
                      ? metaInfo.cellType || "dropdown"
                      : column.cellType;
                  const fieldError =
                    otherQueryFilterErrors?.value?.[matrixRowIndex]?.[
                      column.name
                    ];
                  const fieldTouched =
                    otherQueryFilterTouched?.value?.[matrixRowIndex]?.[
                      column.name
                    ];

                  if (cellType === "dropdown") {
                    return (
                      <DynamicMatrixDropdownCell
                        key={`${column.name}-${String(
                          getChoiceValue(matrixRow[column.name]) ?? "",
                        )}`}
                        column={column}
                        currentValue={matrixRow[column.name]}
                        error={
                          typeof fieldError === "string"
                            ? fieldError
                            : undefined
                        }
                        showLabel={!showHeader}
                        touched={fieldTouched}
                        updateCell={(columnName, value) => {
                          updateCell(matrixRowIndex, columnName, value);
                          setFieldTouched(
                            `otherQueryFilter[${rowIndex}].value[${matrixRowIndex}].${columnName}`,
                            true,
                            false,
                          );
                        }}
                      />
                    );
                  }

                  return (
                    <div className="flex min-w-0 flex-col" key={column.name}>
                      {!showHeader && (
                        <label className="mb-1 text-xs font-semibold text-textNormal">
                          {column.title || column.name}
                          {column.isRequired ? " *" : ""}
                        </label>
                      )}
                      <input
                        className={`h-[42px] w-full rounded border bg-white px-3 text-sm text-textNormal outline-none transition-colors focus:border-primary dark:bg-secondaryLight dark:text-inputText ${fieldTouched && fieldError ? "border-accent_1Dark" : "border-lineDark dark:border-lineLight"}`}
                        name={`otherQueryFilter[${rowIndex}].value[${matrixRowIndex}].${column.name}`}
                        min={column.min}
                        max={column.max}
                        type={column.inputType || "text"}
                        value={matrixRow[column.name] ?? ""}
                        onBlur={handleBlur}
                        onChange={(event) =>
                          updateCell(
                            matrixRowIndex,
                            column.name,
                            (column.inputType || "text") === "number" &&
                              event.target.value !== ""
                              ? Number(event.target.value)
                              : event.target.value,
                          )
                        }
                      />
                      {fieldTouched && typeof fieldError === "string" && (
                        <p className="mt-1 text-xs text-accent_1Dark">
                          {fieldError}
                        </p>
                      )}
                    </div>
                  );
                })}

                {canRemoveRows && (
                  <button
                    aria-label={metaInfo.removeRowText || "Remove row"}
                    className="flex h-10 w-10 items-center justify-center rounded-full text-textMidLight opacity-60 transition-colors hover:bg-primary_5 hover:text-primary hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary_40"
                    title={metaInfo.removeRowText || "Remove row"}
                    type="button"
                    onClick={() => removeRow(matrixRowIndex)}
                  >
                    <TrashIcon fill="currentColor" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {canAddRows && (
        <button
          className="self-start px-0 py-1 text-sm font-semibold text-textLink transition-colors hover:text-primary focus:outline-none focus:underline"
          type="button"
          onClick={addRow}
        >
          {metaInfo.addRowText || "Add row"}
        </button>
      )}

      {typeof otherQueryFilterErrors?.value === "string" && (
        <p className="text-xs text-accent_1Dark">
          {otherQueryFilterErrors.value}
        </p>
      )}
    </div>
  );
};

export default DynamicMatrixFilterInputs;
